import { selectiveCdt1 } from "./selectiveCdt1";
import { planCompression, type CompressionStrategy } from "./compressionPlan";
/** Preserve tree groups, selectively split large CDT1 children, and retain deeper attributes. */
import { buildLayout } from "./buildLayout";
import { type CdtLevelPlan, type LevelRole } from "./cdtLevelPlan";
import { annotateContract, runOffsetsOf } from "./spineInference";
import {
  CollapsedContract,
  CollapsedLevel,
  GeneratedLayout,
  LayoutDiff,
  Orientation,
  PATH_SEPARATOR,
  SpineLevel,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "./studioTypes";

// Colon, not hyphen: item-master values already contain hyphens.
export const COMPOUND_JOIN = " : ";
const CDT1_LEVEL = 1;

/** Strip badges before NFKD so ™ does not become the letters TM. */
export const sanitizeCdtValue = (value: string | null | undefined): string =>
  String(value ?? "")
    .replace(/[®™©]/g, "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”‟]/g, '"')
    .replace(/×/g, "x")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/[:,"\r\n\t]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const splitCompoundValue = (value: string): string[] => value.split(COMPOUND_JOIN);

export interface LevelGroup {
  levels: SpineLevel[];
  orient: Orientation;
  role: LevelRole;
}

export const collapseGroups = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[] = [],
  strategy: CompressionStrategy = {},
): LevelGroup[] =>
  planCompression(items, draft, runs, strategy).levels.map((level) => ({
    levels: level.levels,
    orient: level.orient,
    role: level.role,
  }));

/** Tier attributes per cdt0 branch (keyed by branch, never per item, or two siblings could put
 * different attributes on one tier). A tier the branch cannot fill ships "" rather than
 * pulling a deeper, banded attribute up into a vertical tier. */
const branchTierAttrs = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  plan: CdtLevelPlan,
): ((attrs: Record<string, string | null>) => readonly (readonly string[])[]) => {
  const planAttrs = plan.levels.map((level) => level.levels.map((source) => source.attr));
  const rootAttr = plan.levels[0]?.levels[0]?.attr;
  if (!rootAttr) return () => planAttrs;
  const byBranch = new Map(plan.branches.map((branch) => [branch.value, branch.tiers]));
  return (attrs) => byBranch.get(String(attrs[rootAttr] ?? "")) ?? planAttrs;
};

export interface CollapseResult {
  /** Preserve tier decisions so the panel can explain re-nesting and unresolved ambiguity. */
  plan: CdtLevelPlan;
  contract: CollapsedContract;
  collapsedItems: StudioItem[];
  collapsedDraft: StudioDraft;
  verification: { identical: boolean; orderPreserved: boolean; diffs: LayoutDiff[] };
  generated: GeneratedLayout;
}

const describeSegment = (layout: GeneratedLayout, skuId: string): string => {
  const placement = layout.placements[skuId];
  if (!placement) return "missing";
  return placement.segments
    .map((s) => `r${s.run}s${s.shelf}@${s.xCm.toFixed(0)}×${s.hFacings}`)
    .join(" ");
};

export const collapse4 = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  deepLayout: GeneratedLayout,
  contractWalkOrder: readonly string[] = buildLayout(items, draft, runs).walkOrder,
  strategy: CompressionStrategy = {},
): CollapseResult => {
  const plan = planCompression(items, draft, runs, strategy);
  const groups: LevelGroup[] = plan.levels.map((level) => ({
    levels: level.levels,
    orient: level.orient,
    role: level.role,
  }));

  const levels: CollapsedLevel[] = groups.map((group, index) => ({
    attr: `__cdt${index}`,
    label: group.levels.map((level) => level.label).join(" + "),
    sourceAttrs: group.levels.map((level) => level.attr),
    orient: group.orient,
  }));
  const tierAttrsForBranch = branchTierAttrs(items, draft, plan);

  const selectedChildren =
    strategy.mode !== "measured"
      ? selectiveCdt1(items, draft, runs, plan, contractWalkOrder)
      : new Map<string, string | null>();
  const collapsedItems: StudioItem[] = items.map((item) => {
    const attrs = effectiveAttrs(item, draft);
    const compound: Record<string, string | null> = {};
    const tierAttrs = tierAttrsForBranch(attrs);
    levels.forEach((level, tier) => {
      compound[level.attr] = (tierAttrs[tier] ?? [])
        .map((attr) => sanitizeCdtValue(attrs[attr]))
        .join(COMPOUND_JOIN);
    });
    const child = selectedChildren.get(item.skuId);
    if (child != null)
      compound.__cdt1 = `${compound.__cdt1}${COMPOUND_JOIN}${sanitizeCdtValue(child)}`;
    const placement = deepLayout.placements[item.skuId];
    return {
      ...item,
      attrs: compound,
      placements: (placement?.segments ?? []).map((segment) => ({
        run: segment.run,
        shelf: segment.shelf,
        xCm: segment.xCm,
        widthCm: segment.hFacings * item.facingWidthCm,
        hFacings: segment.hFacings,
      })),
    };
  });

  // Walk order comes from the persisted draft so a release is reproducible.
  const rank = new Map(contractWalkOrder.map((skuId, index) => [skuId, index]));
  const walkSorted = [...collapsedItems].sort(
    (a, b) => (rank.get(a.skuId) ?? 0) - (rank.get(b.skuId) ?? 0),
  );
  const valueOrder: Record<string, string[]> = {};
  for (const level of levels) {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const item of walkSorted) {
      const value = item.attrs[level.attr] ?? "";
      if (!seen.has(value)) {
        seen.add(value);
        order.push(value);
      }
    }
    valueOrder[level.attr] = order;
  }

  const spine2: SpineLevel[] = levels.map((level) => ({
    attr: level.attr,
    label: level.label,
    orient: level.orient,
  }));
  const annotations = {
    scopedValueOrder: {} as Record<string, string[]>,
    bandOverrides: {} as Record<string, number[]>,
    orientOverrides: {} as Record<string, Orientation>,
  };
  annotateContract(collapsedItems, spine2, runOffsetsOf(runs), annotations);

  // Compound values are not globally unique; order per parent like the generator.
  const scopedFromWalk: Record<string, string[]> = {};
  const recordScopedOrders = (
    scopeItems: readonly StudioItem[],
    levelIndex: number,
    path: readonly string[],
  ) => {
    if (levelIndex >= spine2.length || scopeItems.length <= 1) return;
    const attr = spine2[levelIndex].attr;
    const groups = new Map<string, StudioItem[]>();
    for (const item of scopeItems) {
      const value = item.attrs[attr] ?? "";
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    if (groups.size < 2) {
      recordScopedOrders(scopeItems, levelIndex + 1, path);
      return;
    }
    const ordered = [...groups.entries()].sort(
      (a, b) =>
        Math.min(...a[1].map((item) => rank.get(item.skuId) ?? 0)) -
        Math.min(...b[1].map((item) => rank.get(item.skuId) ?? 0)),
    );
    scopedFromWalk[path.join(PATH_SEPARATOR)] = ordered.map(([value]) => value);
    for (const [value, group] of ordered) {
      recordScopedOrders(group, levelIndex + 1, [...path, value]);
    }
  };
  recordScopedOrders(collapsedItems, 0, []);

  const collapsedDraft: StudioDraft = {
    spine: spine2,
    valueOrder,
    scopedValueOrder: scopedFromWalk,
    bandOverrides: annotations.bandOverrides,
    orientOverrides: annotations.orientOverrides,
    shelfHints: Object.fromEntries(
      collapsedItems.map((item) => [
        item.skuId,
        item.placements.map((placement) => ({
          shelf: placement.shelf,
          hFacings: placement.hFacings,
        })),
      ]),
    ),
    attrOverrides: {},
    restrictions: draft.restrictions,
    dismissedFlagIds: [],
    appliedFixFlagIds: [],
  };

  const generated = buildLayout(collapsedItems, collapsedDraft, runs);
  const diffs: LayoutDiff[] = [];
  for (const item of items) {
    const before = describeSegment(deepLayout, item.skuId);
    const after = describeSegment(generated, item.skuId);
    if (before !== after) diffs.push({ skuId: item.skuId, before, after });
  }
  const orderPreserved =
    deepLayout.walkOrder.length === generated.walkOrder.length &&
    deepLayout.walkOrder.every((skuId, index) => generated.walkOrder[index] === skuId);

  const itemValues: Record<string, string[]> = {};
  for (const item of collapsedItems) {
    itemValues[item.skuId] = levels.map((level) => item.attrs[level.attr] ?? "");
  }

  return {
    plan,
    contract: { levels, valueOrder, itemValues },
    collapsedItems,
    collapsedDraft,
    verification: { identical: diffs.length === 0, orderPreserved, diffs },
    generated,
  };
};

/** Priority of each compound value. CDT1 numbers one global gap-free sequence over the
 * (cdt0, cdt1) PAIRS, zone-major (cdt0s in walk order, each cdt0's pairs a contiguous block);
 * deeper levels number 1..n under each parent. Keying by pair, not by bare value, keeps a value
 * that ranges in two cdt0s at each stratum's own wall position. */
export const compoundPriorities = (
  contract: CollapsedContract,
  walkOrder: readonly string[],
): Map<string, number> => {
  const rank = new Map(walkOrder.map((skuId, index) => [skuId, index]));
  const sorted = Object.entries(contract.itemValues).sort(
    (a, b) => (rank.get(a[0]) ?? 0) - (rank.get(b[0]) ?? 0),
  );
  const priorities = new Map<string, number>();
  const counters = new Map<string, number>();

  const cdt0Order: string[] = [];
  const pairFirstRank = new Map<string, number>();
  sorted.forEach(([, values], index) => {
    const cdt0 = values[0] ?? "";
    if (!cdt0Order.includes(cdt0)) cdt0Order.push(cdt0);
    const pairKey = values.slice(0, CDT1_LEVEL + 1).join(PATH_SEPARATOR);
    if (!pairFirstRank.has(pairKey)) pairFirstRank.set(pairKey, index);
  });
  const cdt1Priorities = new Map<string, number>();
  for (const cdt0 of cdt0Order) {
    const ownPairs = [...pairFirstRank.entries()]
      .filter(([key]) => key.split(PATH_SEPARATOR)[0] === cdt0)
      .sort((a, b) => a[1] - b[1]);
    for (const [key] of ownPairs) cdt1Priorities.set(key, cdt1Priorities.size + 1);
  }

  for (const [, values] of sorted) {
    for (let levelIndex = 0; levelIndex < values.length; levelIndex++) {
      const nodeKey = values.slice(0, levelIndex + 1).join(PATH_SEPARATOR);
      if (priorities.has(nodeKey)) continue;
      if (levelIndex === CDT1_LEVEL) {
        priorities.set(nodeKey, cdt1Priorities.get(nodeKey) ?? 0);
        continue;
      }
      const parentKey = values.slice(0, levelIndex).join(PATH_SEPARATOR);
      const next = (counters.get(parentKey) ?? 0) + 1;
      counters.set(parentKey, next);
      priorities.set(nodeKey, next);
    }
  }
  return priorities;
};
